import { ComponentConfig } from '@measured/puck';
import {
  MarginProps,
  marginFields,
} from '../../../../page-builder/puck/reusable-props/NewMargin';
import { useCallback } from 'react';
import {
  ErrorResponse,
  useNavigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import {
  ISelectedUpsellItems,
  postUpsellPurchasePayload,
} from '../../../../page-builder/interface/postUpsellInterface';
import checkoutService from '../../../api/checkoutService';
import {
  setErrorStatus,
  setIsLoading,
} from '../../../../page-builder/redux/slices/postUpsellStateSlice';
import {
  paddingFields,
  PaddingProps,
} from '../../../../page-builder/puck/reusable-props/padding';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../../services/constants';
import { AxiosInstance } from 'axios';
import CustomTooltip from '../../../../components/common/tooltip';
import {
  getBorderProps,
  getcurrentDeviceType,
  getNewMarginStyles,
  getPaddingStyles,
  getQuillEditorText,
} from '../../../utils/helper';
import { QuillTextEditor } from '../../../puck/reusable-props/quill-editor';
import '../../text-editor/style.scss';
import { getIsViewIsEdit, PageType } from '../../../utils/constants';
import showComponent, {
  showComponentProps,
} from '../../../puck/reusable-props/showComponent';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../../puck/reusable-props/Border';
import { backgroungFields } from '../../../puck/reusable-props/backgroungFields';
import {
  setCurrentUpsellData,
  setSelectedProductsForMultiProducts,
} from '../../../redux/slices/postUpsellSlice';
import LoadingSpinner from '../../shared/LoadingSpinner';
import JustifyContent, {
  IJustifyContentProps,
} from '../../blocks/Container/justify-content';
import WidthAndHeight, {
  WidthAndHeightSubProps,
} from '../../../puck/reusable-props/widthAndHeight';
import { gradientFields } from '../../../puck/reusable-props/gradientColor';
import { showNotification } from '../../../api/constant';

export type DeclineButtonProps = {
  NextButton: string;
  border: IBorderValueProps;
  background: {
    color: string;
    isInherit: boolean;
  };
  justifyContent: IJustifyContentProps;
  widthAndHeight: WidthAndHeightSubProps;
  gradient: { isGradientChecked: boolean; color: string };
} & MarginProps &
  PaddingProps &
  showComponentProps;

export interface DeclineButtonComponentProps
  extends ReduxSelectorProps,
    ReduxDispatchProps {
  baseService?: AxiosInstance;
}

export const DeclineButton = ({
  useAppDispatch,
  useAppSelector,
}: DeclineButtonComponentProps): ComponentConfig<DeclineButtonProps> => {
  return {
    label: (
      <CustomTooltip title="Next Button">
        <span>Decline Button</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      NextButton: { ...QuillTextEditor('Button Text', false).content },
      justifyContent: {
        type: 'custom',
        label: 'Justify Content',
        render: ({ value, onChange }) => (
          <JustifyContent
            value={value}
            onChange={onChange}
            title="Justify Content"
          />
        ),
      },
      widthAndHeight: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <WidthAndHeight
            value={value}
            onChange={val => {
              onChange(val);
            }}
            title={'Custom Button'}
            isfullWidth={true}
          />
        ),
      },
      ...backgroungFields,
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      ...gradientFields,
    },
    defaultProps: {
      background: {
        color: '#ffffff',
        isInherit: false,
      },
      widthAndHeight: {
        height: {
          desktop: '100%',
          mobile: '100%',
          tablet: '100%',
        },
        maxWidth: {
          desktop: 'auto',
          mobile: 'auto',
          tablet: 'auto',
        },
        deviceType: 'desktop',
        fullWidth: {
          desktop: false,
          mobile: false,
          tablet: false,
        },
      },
      showComponent: ['desktop', 'tablet', 'mobile'],
      NextButton: `<h1><u style="color: rgb(0, 102, 204); font-size: 20px;">Next Button</u></h1>`,
      gradient: {
        isGradientChecked: false,
        color:
          'linear-gradient(90deg, rgba(255,0,0,1) 0%, rgba(0,0,255,1) 100%)',
      },
      margin: {
        deviceType: 'desktop',
        marginBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        marginTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      ...borderProps,
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      justifyContent: {
        deviceType: 'desktop',
        justify: {
          mobile: 'justify-center',
          tablet: 'justify-center',
          desktop: 'justify-center',
        },
      },
    },
    render: ({
      NextButton,
      margin,
      padding,
      showComponent,
      background,
      widthAndHeight,
      border,
      editMode,
      justifyContent,
      gradient,
    }) => {
      const dispatch = useAppDispatch();
      const navigate = useNavigate();
      const { pathname } = useLocation();
      const checkoutState = useAppSelector(state => state.checkout);
      const postUpsell = useAppSelector(state => state.postUpSell);
      const storeState = useAppSelector(state => state.store);
      const orderID = checkoutState.checkoutOrderID;
      const purchaseToken = checkoutState.purchaseToken;
      const storeData = storeState.storeData;
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getNewMarginStyles(margin, currentDeviceType);
      const [searchParams] = useSearchParams();
      const borderStyles = getBorderProps(border, currentDeviceType);
      const currentUpsellData = postUpsell?.currentUpsellData;
      const upsellData = postUpsell?.upsellData;
      const selectedUpsellItems = postUpsell?.selectedUpsellItems;
      const postUpsellState = useAppSelector(state => state.postUpSellState);
      const isApiLoading = postUpsellState?.isLoading;
      const isCheckoutPaymentPaypal = useAppSelector(
        state => state.common.isPaymentByPaypal,
      );
      const IndependentTransactionForUpsellPages =
        storeData?.IndependentTransactionForUpsellPages ||
        isCheckoutPaymentPaypal ||
        false;

      const state = useAppSelector(state => state);
      const newNextButton = getQuillEditorText(NextButton, state);
      const newSearchParams = new URLSearchParams(searchParams.toString());
      const smartUpsellFunnelID = useAppSelector(
        state => state.postUpSell.smartUpsellFunnelID,
      );
      const IsViewIsEdit = getIsViewIsEdit();
      const isUpsellFunnelPreview = searchParams.get('isUpsellFunnelPreview');

      let navState = {};
      if (!getIsViewIsEdit() && checkoutState?.nextpageConfig?.id) {
        navState = {
          state: checkoutState?.nextpageConfig?.id
            ? {
                id: checkoutState?.nextpageConfig?.id,
                pageVisiblity: checkoutState?.nextpageConfig?.pageVisiblity,
                pageType: checkoutState?.nextpageConfig?.pageType,
              }
            : {},
        };
      }

      const navigateFunction = useCallback(() => {
        if (checkoutState?.nextpageConfig?.id) {
          navigate(pathname + '?' + newSearchParams.toString(), navState);
        }
      }, [
        checkoutState.nextpageConfig?.id,
        checkoutState.nextpageConfig?.pageVisiblity,
      ]);

      let declineNavState = {};
      if (
        !getIsViewIsEdit() &&
        upsellData?.[currentUpsellData?.decline?.nextProduct]?.pageId
      ) {
        declineNavState = {
          state: upsellData?.[currentUpsellData?.decline?.nextProduct]?.pageId
            ? {
                id: upsellData?.[currentUpsellData?.decline?.nextProduct]
                  ?.pageId,
                pageVisiblity: '',
                pageType: PageType.UPSELL,
              }
            : {},
        };
      }

      const navigateDownsellFunction = useCallback(() => {
        if (upsellData?.[currentUpsellData?.decline?.nextProduct]?.pageId) {
          navigate(
            pathname + '?' + newSearchParams.toString(),
            declineNavState,
          );
        }
      }, [upsellData?.[currentUpsellData?.decline?.nextProduct]?.pageId]);

      const openEditor = async () => {
        const newPageID = upsellData?.[currentUpsellData?.decline?.nextProduct]
          ?.pageId
          ? upsellData?.[currentUpsellData?.decline?.nextProduct]?.pageId
          : upsellData?.[currentUpsellData?.accept?.nextProduct]?.pageId;
        const updatedSearchParams = new URLSearchParams(
          searchParams.toString(),
        );
        updatedSearchParams.set('pageID', String(newPageID));
        navigate(pathname + '?' + updatedSearchParams.toString());
      };

      // const newShopUrl = storeData?.ShopURL?.replace('https://', '');
      // const url = `/confirmation-page?store=${newShopUrl}`;
      const handleCompletePurchase = useCallback(
        async (selectedItems: ISelectedUpsellItems[]) => {
          if (
            selectedItems.length === 0 ||
            IndependentTransactionForUpsellPages
          ) {
            navigateFunction();
            return;
          }

          const payload: postUpsellPurchasePayload = {
            LineItems: selectedItems
              .filter(item => item)
              .map(item => ({
                Quantity: 1,
                VariantID: item.variantId,
                UpsellDetails: {
                  GroupId: item.GroupId,
                  IsDownsell: item.IsDownsell,
                  DownsellId: item.IsDownsell ? (item.DownsellId ?? '') : '',
                  ProductId: item.ProductId,
                },
              })),
            OrderID: orderID,
            PurchaseToken: purchaseToken,
            ShopID: storeData?.ShopID,
            StoreID: storeData?.StoreID || '',
            FunnelId: Number(smartUpsellFunnelID),
          };

          try {
            const res = await checkoutService.postUpsellPurchase(payload);

            if (res.status === 200) {
              dispatch(setIsLoading(false));
              navigateFunction();
            }
          } catch (error: unknown) {
            const err = error as ErrorResponse;
            if (err.status === 400) {
              dispatch(setIsLoading(false));
              dispatch(setErrorStatus(err.status));
              navigateFunction();
              throw error;
            } else {
              dispatch(setIsLoading(false));
              navigateFunction();
              throw error;
            }
          }
        },
        [navigate, navigateFunction, orderID, purchaseToken, storeData],
      );
      async function handleClick() {
        dispatch(setIsLoading(true));
        if (postUpsell?.predefinedUpsellProduct) {
          dispatch(setSelectedProductsForMultiProducts([]));
        }

        if (!currentUpsellData?.decline?.nextProduct) {
          try {
            IsViewIsEdit && !!isUpsellFunnelPreview
              ? showNotification('success', 'Upsell flow ended here')
              : await handleCompletePurchase(selectedUpsellItems);
            dispatch(setIsLoading(false));
          } catch (error) {
            dispatch(setIsLoading(false));
          }
        } else {
          IsViewIsEdit && !!isUpsellFunnelPreview
            ? await openEditor()
            : navigateDownsellFunction();
          dispatch(
            setCurrentUpsellData(
              upsellData?.[currentUpsellData?.decline?.nextProduct],
            ),
          );
          dispatch(setIsLoading(false));
        }
      }
      // const handleNextRecommendation = useCallback(() => {
      //   dispatch(setIsLoading(true));
      //   setTimeout(() => {
      //     if (
      //       postUpsellState?.currentIndex + 1 >=
      //       postUpSellRecommendation?.length
      //     ) {
      //       handleCompletePurchase(postUpsellState?.selectedItems);
      //       dispatch(setIsLoading(false));
      //     } else {
      //       dispatch(setCurrentIndex(postUpsellState?.currentIndex + 1));
      //       dispatch(setIsLoading(false));
      //     }
      //   }, 1000);
      // }, [
      //   setCurrentIndex,
      //   postUpsellState.currentIndex,
      //   postUpSellRecommendation,
      //   navigate,
      //   navigateFunction,
      //   postUpsellState.selectedItems,
      //   handleCompletePurchase,
      // ]);
      const containerStyle: React.CSSProperties = {
        background: gradient?.isGradientChecked
          ? gradient?.color
          : background?.isInherit
            ? 'inherit'
            : background?.color,
      };

      const buttonSize: React.CSSProperties =
        currentDeviceType &&
        widthAndHeight.maxWidth &&
        widthAndHeight.height &&
        widthAndHeight.maxWidth[currentDeviceType] !== undefined &&
        widthAndHeight.height[currentDeviceType] !== undefined
          ? {
              width: widthAndHeight?.fullWidth?.[currentDeviceType]
                ? '100%'
                : isNaN(+widthAndHeight.maxWidth[currentDeviceType])
                  ? widthAndHeight.maxWidth[currentDeviceType]
                  : `${+widthAndHeight.maxWidth[currentDeviceType]}px`,

              height: isNaN(+widthAndHeight.height[currentDeviceType])
                ? widthAndHeight.height[currentDeviceType]
                : `${+widthAndHeight.height[currentDeviceType]}px`,
            }
          : {};
      return (
        <>
          {showComponent?.includes(currentDeviceType) && (
            <div
              className={`flex text-editor-content ${justifyContent?.justify?.[currentDeviceType]}`}>
              <button
                disabled={isApiLoading}
                onClick={() => handleClick()}
                className={`${border?.borderStyle?.[currentDeviceType]}`}
                style={{
                  lineHeight: 1.5,
                  ...containerStyle,
                  ...marginStyles,
                  ...getPaddingStyles(padding, currentDeviceType),
                  ...borderStyles,
                  ...buttonSize,
                }}
                dangerouslySetInnerHTML={{ __html: newNextButton }}></button>
              {isApiLoading && (
                <div className="absolute inset-0 cursor-not-allowed flex justify-center items-center bg-white bg-opacity-90">
                  <div className="h-9">
                    <LoadingSpinner />
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      );
    },
  };
};
